import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Clock } from "lucide-react";
import DOMPurify from "dompurify";
import { SeoHead } from "@/components/SeoHead";
import { absoluteSeoUrl, limitSeoText } from "@/lib/seo";

const useCountdown = (endDate) => {
  const [t, setT] = useState({ d: 0, h: 0, m: 0, s: 0 });
  useEffect(() => {
    if (!endDate) return;
    const end = new Date(endDate).getTime();
    const tick = () => {
      const diff = end - Date.now();
      if (diff <= 0) return setT({ d: 0, h: 0, m: 0, s: 0 });
      setT({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff % 86400000) / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const i = setInterval(tick, 1000);
    return () => clearInterval(i);
  }, [endDate]);
  return t;
};

const Campaign = () => {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  const [err, setErr] = useState(null);
  const cd = useCountdown(c?.end_date);

  useEffect(() => {
    setC(null);
    setErr(null);
    api.get(`/campaigns/${slug}`)
      .then((response) => setC(response.data))
      .catch(() => setErr("This campaign is no longer live."));
  }, [slug]);

  if (err) {
    return (
      <div className="mx-auto max-w-2xl py-20 text-center">
        <p className="text-[#64748B]">{err}</p>
        <Link to="/" className="mt-4 inline-block font-medium text-[#C86D42]">
          Back to home →
        </Link>
      </div>
    );
  }
  if (!c) {
    return <div className="py-20 text-center">Loading…</div>;
  }

  const seo = {
    title: `${c.title} | Eira Products`,
    description: limitSeoText(c.tagline || c.title),
    canonical: absoluteSeoUrl(`/campaign/${c.slug || slug}`),
    image: c.hero_image || "",
    type: "website",
  };

  return (
    <>
      <SeoHead seo={seo} />
      <div data-testid="campaign-page">
        <section
          className="relative min-h-[520px] overflow-hidden"
          style={{ background: c.theme_color || "#2D5A47" }}
        >
          {c.hero_video ? (
            <video
              src={c.hero_video}
              autoPlay
              muted
              loop
              playsInline
              className="absolute inset-0 h-full w-full object-cover opacity-70"
            />
          ) : (
            <img
              src={c.hero_image}
              alt={c.title}
              decoding="async"
              className="absolute inset-0 h-full w-full object-cover opacity-70"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/20" />
          <div
            className="relative mx-auto max-w-7xl px-4 py-20 text-white sm:px-8 lg:px-12 lg:py-28"
          >
            <div className="mb-4 text-xs uppercase tracking-[0.3em] text-[#D99B26]">
              Eira Campaign
            </div>
            <h1 className="max-w-3xl font-serif text-5xl font-semibold leading-[1.02] lg:text-7xl">
              {c.title}
            </h1>
            {c.tagline && (
              <p className="mt-5 max-w-2xl text-lg leading-relaxed text-white/80">
                {c.tagline}
              </p>
            )}
            {c.end_date && (
              <div className="mt-8 flex items-center gap-3 text-white/90">
                <Clock size={16} />
                <span className="text-xs uppercase tracking-widest">Ends in</span>
                <div className="flex gap-2 font-mono">
                  {[
                    { l: "D", v: cd.d },
                    { l: "H", v: cd.h },
                    { l: "M", v: cd.m },
                    { l: "S", v: cd.s },
                  ].map((item) => (
                    <div
                      key={item.l}
                      className="rounded bg-white/15 px-3 py-1 text-sm font-bold"
                    >
                      {String(item.v).padStart(2, "0")}
                      <span className="ml-1 text-[10px] text-white/60">{item.l}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {c.cta_link && (
              <a
                href={c.cta_link}
                className="mt-8 inline-block rounded-lg bg-[#C86D42] px-8 py-3.5 font-semibold shadow-lg hover:bg-[#AE5931]"
                data-testid="campaign-cta"
              >
                {c.cta_text || "Shop Now"} →
              </a>
            )}
          </div>
        </section>

        {c.body_html && (
          <section
            className="prose mx-auto max-w-4xl px-4 py-12 text-[#1E2A24]"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(c.body_html) }}
          />
        )}

        <section className="mx-auto max-w-7xl px-4 py-12 sm:px-8 lg:px-12">
          <div className="mb-8 flex items-end justify-between">
            <div>
              <div className="mb-2 text-xs uppercase tracking-[0.25em] text-[#8C6D46]">
                Featured in this campaign
              </div>
              <h2 className="font-serif text-3xl font-semibold lg:text-4xl">
                Handpicked Selection
              </h2>
            </div>
          </div>
          {c.products?.length > 0 ? (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 lg:gap-6">
              {c.products.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          ) : (
            <div className="py-16 text-center text-[#64748B]">
              Products coming soon for this campaign.
            </div>
          )}
        </section>
      </div>
    </>
  );
};

export default Campaign;
